import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { finishActivity, myOngoing } from "../lib/api";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toLocalDateInputValue(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toLocalTimeInputValue(d) {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function fromLocalDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const dt = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function hashStringToInt(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function stableRand01(seed, i) {
  const x = Math.sin((i + 1) * 127.1 + seed * 0.013) * 43758.5453123;
  return x - Math.floor(x);
}

export default function FinalizeActivity() {
  const navigate = useNavigate();
  const [ongoing, setOngoing] = useState(null);
  const [loading, setLoading] = useState(true);

  const now = useMemo(() => new Date(), []);
  const [dateStr, setDateStr] = useState(() => toLocalDateInputValue(now));
  const [timeStr, setTimeStr] = useState(() => toLocalTimeInputValue(now));
  const [squares, setSquares] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [falling, setFalling] = useState([]);
  const [poofs, setPoofs] = useState([]);
  const idSeq = useRef(1);

  const footerRef = useRef(null);
  const [footerH, setFooterH] = useState(104);
  const pileH = 120;

  useEffect(() => {
    const measure = () => {
      if (!footerRef.current) return;
      const h = footerRef.current.getBoundingClientRect().height;
      if (h && Number.isFinite(h)) setFooterH(Math.max(88, Math.round(h)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const mo = await myOngoing();
        if (!alive) return;
        if (mo && mo.id) {
          setOngoing(mo);
          if (typeof mo.paperSquaresUsed === "number") setSquares(mo.paperSquaresUsed);
        } else {
          setOngoing(null);
        }
      } catch {
        if (!alive) return;
        setOngoing(null);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, []);

  const spawnFalling = useCallback((count) => {
    const n = Math.min(8, Math.max(0, count));
    if (n === 0) return;
    const created = [];
    for (let i = 0; i < n; i += 1) {
      const id = idSeq.current++;
      created.push({
        id,
        left: 6 + Math.random() * 88,
        delayMs: Math.floor(Math.random() * 140),
        durMs: 650 + Math.floor(Math.random() * 350),
        spin: (Math.random() * 120 - 60).toFixed(0)
      });
    }
    setFalling((prev) => prev.concat(created));
    for (const f of created) {
      window.setTimeout(() => {
        setFalling((prev) => prev.filter((x) => x.id !== f.id));
      }, f.delayMs + f.durMs + 40);
    }
  }, []);

  const spawnPoof = useCallback((count) => {
    const n = Math.min(8, Math.max(0, count));
    if (n === 0) return;
    const created = [];
    for (let i = 0; i < n; i += 1) {
      const id = idSeq.current++;
      created.push({
        id,
        left: 10 + Math.random() * 80,
        delayMs: Math.floor(Math.random() * 80),
        durMs: 420 + Math.floor(Math.random() * 180)
      });
    }
    setPoofs((prev) => prev.concat(created));
    for (const p of created) {
      window.setTimeout(() => {
        setPoofs((prev) => prev.filter((x) => x.id !== p.id));
      }, p.delayMs + p.durMs + 40);
    }
  }, []);

  const adjust = useCallback(
    (delta) => {
      setError(null);
      setSquares((prev) => {
        const next = Math.max(0, prev + delta);
        return next;
      });
      if (delta > 0) spawnFalling(delta);
      if (delta < 0) spawnPoof(-delta);
    },
    [spawnFalling, spawnPoof]
  );

  const mound = useMemo(() => {
    const maxRender = 120;
    const renderCount = Math.min(maxRender, Math.max(0, squares));
    const capped = squares > maxRender;

    const cols = 24;
    const center = (cols - 1) / 2;

    const seedStr = ongoing?.id ? String(ongoing.id) : "seed";
    const seed = hashStringToInt(seedStr);

    const weights = [];
    let sumW = 0;

    for (let i = 0; i < cols; i += 1) {
      const dist = Math.abs(i - center) / center;
      const moundBase = Math.pow(Math.max(0, 1 - dist), 1.7);
      const base = 0.12 + 0.88 * moundBase;
      const wobble = (stableRand01(seed, i) - 0.5) * 0.18;
      const w = Math.max(0.001, base * (1 + wobble));
      weights.push(w);
      sumW += w;
    }

    const heights = new Array(cols).fill(0);
    const fracs = new Array(cols).fill(0);

    let used = 0;
    for (let i = 0; i < cols; i += 1) {
      const exact = (renderCount * weights[i]) / sumW;
      const flo = Math.floor(exact);
      heights[i] = flo;
      fracs[i] = exact - flo;
      used += flo;
    }

    let remaining = renderCount - used;
    if (remaining > 0) {
      const order = [];
      for (let i = 0; i < cols; i += 1) order.push(i);
      order.sort((a, b) => fracs[b] - fracs[a]);
      let k = 0;
      while (remaining > 0) {
        const idx = order[k % order.length];
        heights[idx] += 1;
        remaining -= 1;
        k += 1;
      }
    }

    return { cols, heights, capped, extra: squares - renderCount };
  }, [squares, ongoing]);

  const submit = useCallback(async () => {
    if (!ongoing?.id || saving) return;
    const dt = fromLocalDateTime(dateStr, timeStr);
    if (!dt) {
      setError("Selecciona una fecha y hora válidas.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await finishActivity(ongoing.id, squares, dt.toISOString());
      navigate("/");
    } catch (e) {
      const msg = String(e?.message || "");
      if (msg.includes("400")) {
        setError("La hora seleccionada tiene que estar a menos de 24h de ahora.");
      } else {
        setError("No se pudo finalizar. Intenta de nuevo.");
      }
      setSaving(false);
    }
  }, [ongoing, saving, dateStr, timeStr, squares, navigate]);

  return (
    <div
      className="min-h-screen bg-white text-black flex flex-col relative overflow-hidden"
      style={{
        "--footerOffset": `${footerH}px`,
        "--pileHeight": `${pileH}px`
      }}
    >
      <style>{`
        @keyframes fall {
          0% { transform: translate(-50%, -28px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate(-50%, calc(100vh - var(--footerOffset) - 10px)) rotate(var(--spin)); opacity: 1; }
        }
        @keyframes poof {
          0% { transform: translate(-50%, 0) scale(1); opacity: 0.9; }
          100% { transform: translate(-50%, -28px) scale(0.4); opacity: 0; }
        }
      `}</style>

      {falling.map((f) => (
        <div
          key={f.id}
          style={{
            position: "absolute",
            top: 0,
            left: `${f.left}%`,
            fontSize: 18,
            animation: `fall ${f.durMs}ms ease-in ${f.delayMs}ms forwards`,
            pointerEvents: "none",
            zIndex: 10,
            "--spin": `${f.spin}deg`
          }}
        >
          ⬜️
        </div>
      ))}

      {poofs.map((p) => (
        <div
          key={p.id}
          style={{
            position: "fixed",
            left: `${p.left}%`,
            bottom: `calc(var(--footerOffset) + 10px)`,
            fontSize: 18,
            animation: `poof ${p.durMs}ms ease-out ${p.delayMs}ms forwards`,
            pointerEvents: "none",
            zIndex: 11
          }}
        >
          ⬜️
        </div>
      ))}

      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <button onClick={() => navigate("/activity")} className="rounded-full border px-4 py-2 text-sm">
          Chat
        </button>
        <button onClick={() => navigate("/")} className="rounded-full border px-4 py-2 text-sm">
          Dashboard
        </button>
      </div>

      <div className="flex-1 min-h-0 px-4 pb-[260px]">
        {loading ? (
          <div className="h-full border border-dashed border-neutral-300 rounded-2xl flex items-center justify-center text-neutral-500">
            cargando…
          </div>
        ) : !ongoing?.id ? (
          <div className="h-full border border-dashed border-neutral-300 rounded-2xl flex items-center justify-center text-neutral-500">
            no hay actividad en curso
          </div>
        ) : (
          <div className="relative h-full border border-neutral-200 rounded-2xl bg-white overflow-hidden flex flex-col">
            <div className="px-5 pt-5 pb-4">
              <div className="text-sm text-neutral-600">¿A qué hora terminaste?</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <input
                  type="date"
                  value={dateStr}
                  onChange={(e) => setDateStr(e.target.value)}
                  className="rounded-xl border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none"
                />
                <input
                  type="time"
                  value={timeStr}
                  onChange={(e) => setTimeStr(e.target.value)}
                  className="rounded-xl border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none"
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-7 px-6 pt-3 pb-6">
              <div className="text-5xl font-mono select-none leading-none mt-2">{squares}</div>

              <div className="text-sm text-neutral-600">papeles</div>

              <div className="w-full max-w-[440px]">
                <div className="grid grid-cols-4 gap-3">
                  <button onClick={() => adjust(-1)} className="rounded-xl border px-4 py-3.5 text-base active:scale-95 transition">
                    −1
                  </button>
                  <button onClick={() => adjust(1)} className="rounded-xl border px-4 py-3.5 text-base active:scale-95 transition">
                    +1
                  </button>
                  <button onClick={() => adjust(2)} className="rounded-xl border px-4 py-3.5 text-base active:scale-95 transition">
                    +2
                  </button>
                  <button onClick={() => adjust(5)} className="rounded-xl border px-4 py-3.5 text-base active:scale-95 transition">
                    +5
                  </button>
                </div>
              </div>

              {error ? <div className="text-sm text-red-600 text-center">{error}</div> : null}
            </div>
          </div>
        )}
      </div>

      <div
        className="fixed left-0 right-0 pointer-events-none"
        style={{
          bottom: `var(--footerOffset)`,
          height: `var(--pileHeight)`,
          zIndex: 9
        }}
      >
        <div className="h-full flex items-end justify-center px-4 pb-2">
          <div className="w-full max-w-[540px]">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${mound.cols}, minmax(0, 1fr))`,
                alignItems: "end",
                height: "100%"
              }}
            >
              {mound.heights.map((h, colIdx) => (
                <div
                  key={colIdx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    height: "100%"
                  }}
                >
                  {Array.from({ length: h }).map((_, i) => (
                    <span key={i} style={{ fontSize: 14, lineHeight: "14px" }}>
                      ⬜️
                    </span>
                  ))}
                </div>
              ))}
            </div>

            {mound.capped ? (
              <div className="text-xs text-neutral-500 text-center mt-1">+{mound.extra}</div>
            ) : null}
          </div>
        </div>
      </div>

      <div ref={footerRef} className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-neutral-200 px-4 py-3">
        <div className="max-w-[540px] mx-auto">
          <button
            onClick={submit}
            disabled={!ongoing?.id || saving}
            className={`w-full rounded-2xl px-4 py-4 text-lg ${saving ? "bg-neutral-400 text-white" : "bg-black text-white"}`}
          >
            {saving ? "…" : "Finalizar"}
          </button>
        </div>
      </div>
    </div>
  );
}
